import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

const formatCountdown = (seconds) => {
  const secs = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const HalfDayLeaveWarningModal = ({
  leaveStatus,
  secondsUntilStart,
  onClose,
  onCancelLeave,
  onAcknowledge,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const title = useMemo(() => {
    if (leaveStatus?.halfDayStarted || (secondsUntilStart != null && secondsUntilStart <= 0)) {
      return 'Half day leave started';
    }
    return 'Half day leave starting soon';
  }, [leaveStatus?.halfDayStarted, secondsUntilStart]);

  const handleCancel = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onCancelLeave();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to cancel leave');
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-3 sm:p-5 bg-black/55 backdrop-blur-[2px]">
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl shadow-2xl border border-amber-200/70 dark:border-amber-800 bg-white dark:bg-gray-800">
        <div className="px-5 pt-5 pb-4 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white">
          <p className="text-amber-50/90 text-xs font-medium">Leave reminder</p>
          <h2 className="text-xl font-bold tracking-tight mt-1">{title}</h2>
          {secondsUntilStart != null && secondsUntilStart > 0 && (
            <p className="mt-3 text-3xl font-bold tabular-nums">{formatCountdown(secondsUntilStart)}</p>
          )}
        </div>
        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {leaveStatus?.halfDayStarted || (secondsUntilStart != null && secondsUntilStart <= 0)
              ? 'Logout will start automatically now for your half day leave.'
              : `Your half day leave starts at ${leaveStatus?.startTimeLabel || leaveStatus?.startTime || 'the scheduled time'}. Logout will start automatically when it begins.`}
          </p>
          {leaveStatus?.reason && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Reason: {leaveStatus.reason}
            </p>
          )}
        </div>
        <div className="px-5 pb-5 flex gap-2.5">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="flex-1 rounded-xl border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-100 text-sm font-semibold py-3 hover:bg-amber-50 dark:hover:bg-amber-950/40 disabled:opacity-70"
          >
            {submitting ? 'Cancelling…' : 'Cancel leave'}
          </button>
          <button
            type="button"
            onClick={() => {
              onAcknowledge?.();
              onClose();
            }}
            disabled={submitting}
            className="flex-1 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold py-3 disabled:opacity-70"
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default HalfDayLeaveWarningModal;
